import React, { useMemo, useState } from "react";
import { Sparkles, Bot, Lock, ChevronDown, ChevronUp, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { useRecommendations } from "../context/RecommendationsContext";
import { useAuth } from "../context/AuthContext";
import LotteryLogo from "./LotteryLogo";
import AuthModal from "./AuthModal";

function PaleBall({ value, accent = "blue" }) {
  const palette = {
    blue:    "from-[#1d6cb1] to-[#0e4a7a]",
    gold:    "from-yellow-400 via-orange-400 to-red-500",
    emerald: "from-emerald-500 to-teal-600",
    purple:  "from-fuchsia-500 to-purple-700",
  };
  return (
    <span
      className={`inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br ${palette[accent]} text-white font-extrabold text-xl shadow-md border border-white/20`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {value}
    </span>
  );
}

export default function PerLotteryPales() {
  const { data, loading } = useRecommendations();
  const { isAuthenticated } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const grouped = useMemo(() => {
    const list = data?.per_lottery_pales || [];
    const map = {};
    for (const p of list) {
      map[p.lottery_id] = map[p.lottery_id] || { name: p.lottery_name, color: p.lottery_color, items: [] };
      map[p.lottery_id].items.push(p);
    }
    return map;
  }, [data]);

  const total = data?.per_lottery_pales?.length || 0;
  if (loading || total === 0) return null;

  const locked = !isAuthenticated;

  return (
    <section id="pale-por-loteria" className="py-14 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold mb-3 border border-amber-100">
            <Bot className="w-3.5 h-3.5" /> PROMETEO · Análisis Matemático Personalizado
          </div>
          <h2 className="np-title inline-block text-2xl sm:text-3xl font-extrabold text-[#0e4a7a]">
            Palé Recomendado para Cada Lotería
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto mt-3 text-sm sm:text-base">
            Prometeo analizó los últimos sorteos de cada lotería (frecuencia, hot/cold, palés históricos y numerología del día) y generó un palé personalizado para cada una.
          </p>
        </div>

        {locked ? (
          <div className="relative max-w-2xl mx-auto rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 sm:p-8 text-center">
            <div className="absolute inset-0 backdrop-blur-sm rounded-2xl" />
            <div className="relative">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-500 to-red-500 text-white flex items-center justify-center shadow-lg">
                <Lock className="w-7 h-7" />
              </div>
              <h3 className="font-extrabold text-lg text-[#0e4a7a]">Exclusivo para Registrados</h3>
              <p className="text-sm text-gray-700 mt-2 max-w-md mx-auto">
                Tenemos un palé personalizado para cada una de las {total} jugadas de hoy. Regístrate gratis para verlos todos.
              </p>
              <button
                onClick={() => setAuthOpen(true)}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-red-500 text-white font-bold shadow-md hover:shadow-lg transition"
              >
                Registrarme Gratis
              </button>
            </div>
            <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab="register" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(grouped)
                .slice(0, expanded ? undefined : 4)
                .map(([lotId, lot]) => (
                  <div key={lotId} className="rounded-2xl border-2 border-blue-100 bg-white shadow-sm overflow-hidden">
                    <div
                      className="px-4 py-2 text-sm font-extrabold tracking-wide flex items-center gap-2"
                      style={{ background: lot.color, color: "#fff" }}
                    >
                      <Trophy className="w-4 h-4" />
                      {lot.name}
                    </div>
                    <div className="p-4 divide-y divide-blue-50">
                      {lot.items.map((p) => (
                        <div key={p.draw_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`pale-${p.lottery_id}-${p.draw_id}`}>
                          <div className="flex items-center gap-3 flex-1 min-w-[180px]">
                            <LotteryLogo lottery={{ id: p.lottery_id, name: p.lottery_name }} draw={{ id: p.draw_id, name: p.draw_name }} size={42} />
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{p.draw_name}</p>
                              <p className="text-[11px] text-gray-500">
                                {p.draw_time ? `${p.draw_time} · ` : ""}Confianza {p.confidence}%
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {p.pale.split("-").map((b, i) => (
                              <PaleBall
                                key={i}
                                value={b}
                                accent={["blue", "gold", "emerald", "purple"][i % 4]}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            {Object.keys(grouped).length > 4 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-50 text-[#1d6cb1] font-bold text-sm hover:bg-blue-100 transition border border-blue-200"
                  data-testid="toggle-all-pales"
                >
                  {expanded ? (
                    <>Ver menos <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Ver todas las {Object.keys(grouped).length} loterías <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            )}

            <div className="mt-8 text-center">
              <Link
                to="/tauro"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-red-500 text-white font-bold shadow-md hover:shadow-lg transition text-sm"
              >
                <Sparkles className="w-4 h-4" />
                ¿Quieres más profundidad? Habla con Tauro
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
